import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarView } from './Sidebar';
import { useContadoresDelMenu, useMenu } from '@/hooks/useMenu';
import { useEsEscritorio } from '@/hooks/useEsEscritorio';
import { useUiStore } from '@/store/ui.store';
import { cn } from '@/lib/cn';

/**
 * EL MENÚ DEL TELÉFONO (13-oct-2026).
 *
 * Pedido: «en teléfono el menú arriba a la izquierda (la hamburguesa) no funciona, no
 * despliega; los contenidos que deberían estar en el desplegado están en la parte de abajo».
 * En el teléfono el lateral está escondido y la hamburguesa lo plegaba igual: no pasaba nada.
 * Abajo había una barra fija de cinco accesos, y el resto del menú (Ventas, Productos,
 * Inventario, Facturas…) no tenía entrada.
 *
 * Ahora la hamburguesa abre este panel desde la izquierda, con el MISMO menú del lateral (el que
 * sirve el servidor para quien mira): mismas pantallas, mismo orden, misma marca. Se cierra con
 * la «X», tocando fuera, con Esc o al elegir una pantalla.
 */

const ENFOCABLES = 'a[href], button:not([disabled])';

export function MenuMovil() {
  const abierto = useUiStore((s) => s.menuMovilAbierto);
  const cerrar = useUiStore((s) => s.cerrarMenuMovil);
  const escritorio = useEsEscritorio();
  const { datos, cargando, fallo, sinNegocio } = useMenu();
  const contadores = useContadoresDelMenu(datos);
  const { pathname } = useLocation();
  const panel = useRef<HTMLDivElement>(null);

  // Se abrió para ir a algún lado: al llegar, se cierra (también si se llegó con «atrás»).
  // Y si la pantalla pasa a escritorio con el menú abierto, también: allá está el lateral.
  useEffect(() => {
    cerrar();
  }, [pathname, cerrar]);
  useEffect(() => {
    if (escritorio) cerrar();
  }, [escritorio, cerrar]);

  // Abierto: el foco entra a la «X», Tab no se sale del panel y Esc lo cierra (sin que Esc
  // haga además lo suyo en la pantalla de atrás). Al cerrar, el foco vuelve a la hamburguesa.
  useEffect(() => {
    if (!abierto) return;
    const antes = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>('[data-cerrar-menu]')?.focus();
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cerrar();
        return;
      }
      const caja = panel.current;
      if (e.key !== 'Tab' || !caja) return;
      const lista = [...caja.querySelectorAll<HTMLElement>(ENFOCABLES)];
      const primero = lista[0];
      const ultimo = lista[lista.length - 1];
      // Si algo sacó el foco del panel (un atajo de teclado), Tab lo devuelve adentro.
      if (!caja.contains(document.activeElement)) {
        e.preventDefault();
        primero?.focus();
      } else if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo?.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero?.focus();
      }
    };
    window.addEventListener('keydown', alTeclear, true);
    return () => {
      window.removeEventListener('keydown', alTeclear, true);
      antes?.focus?.();
    };
  }, [abierto, cerrar]);

  if (escritorio) return null;

  return (
    <div
      id="menu-movil"
      className={cn(
        'fixed inset-0 z-50 transition-[visibility] duration-200 motion-reduce:duration-0',
        abierto ? 'visible' : 'invisible',
      )}
    >
      <div
        aria-hidden
        onClick={cerrar}
        // `touch-none`: arrastrar el dedo sobre el fondo no mueve la pantalla de atrás (iPhone).
        className={cn(
          'mac-velo absolute inset-0 touch-none transition-opacity duration-200 motion-reduce:transition-none',
          abierto ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        ref={panel}
        // Diálogo solo mientras está abierto: cerrado sigue en la página (para la animación), y un
        // `role="dialog"` escondido hacía creer a las pestañas que había una ventana encima (Esc).
        role={abierto ? 'dialog' : undefined}
        aria-modal={abierto ? true : undefined}
        aria-label="Menú"
        // Tocar una pantalla cierra el menú aunque sea la que ya está abierta.
        onClick={(e) => {
          if ((e.target as Element).closest('a')) cerrar();
        }}
        className={cn(
          'absolute inset-y-0 left-0 flex transition-transform duration-200 ease-out motion-reduce:transition-none',
          abierto ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <SidebarView
          sidebarOpen
          contadores={contadores}
          datos={datos}
          cargando={cargando}
          fallo={fallo}
          sinNegocio={sinNegocio}
          alCerrar={cerrar}
          className={cn(
            'mac-hoja h-full w-72 max-w-[85vw] rounded-r-3xl bg-[var(--mac-vidrio-globo)]',
            // La muesca y la barra de inicio del teléfono no tapan ni el logo ni la última pantalla.
            'pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pt-[env(safe-area-inset-top)]',
          )}
        />
      </div>
    </div>
  );
}
